import React, { useEffect, useState } from 'react';
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, TextField } from '@mui/material';
import { Facility, FREQUENCIES, REPAYMENT_TYPES, RepaymentType, ScheduleFrequency } from '../../interfaces/borrowing.interface';
import { BorrowingService } from '../../services/borrowing.service';
import { ExportLcService } from '../../services/exportLc.service';
import SearchableSelect from '../common/SearchableSelect';
import { useToast } from '../../context/ToastContext';
import { toLocalIsoDate } from '../../utils/dateUtils';
import { humanizeEnum } from '../../utils/format';
import { useFinancingLookups } from './useFinancingLookups';

const cell = { flex: '1 1 200px', maxWidth: 300 } as const;

const DrawdownDialog: React.FC<{ open: boolean; facility: Facility; onClose: () => void; onSaved: () => void }> = ({ open, facility, onClose, onSaved }) => {
  const toast = useToast();
  const { bankAccounts } = useFinancingLookups(facility.institutionId);
  const [f, setF] = useState<Record<string, string>>({});
  const [opening, setOpening] = useState(false);
  const [saving, setSaving] = useState(false);
  // W3-B1: a packing-credit drawdown is drawn against an export LC (position shows the eligibility headroom).
  const isPc = facility.facilityType === 'PACKING_CREDIT';
  const [lcs, setLcs] = useState<{ id: string; label: string }[]>([]);
  const [exportLcId, setExportLcId] = useState('');
  const foreign = !!facility.currencyCode && facility.currencyCode !== 'BDT';

  useEffect(() => {
    if (!open || !isPc) return;
    let live = true;
    ExportLcService.list(facility.institutionId, { size: 200 })
      .then(r => {
        if (live) setLcs(r.content.filter(x => x.status !== 'CLOSED' && x.status !== 'REALIZED')
          .map(x => ({ id: x.id, label: `${x.lcNumber} - ${x.buyerName} (${x.currencyCode} ${x.lcAmount})` })));
      })
      .catch(err => toast.showApiError(err, 'Could not load the export LCs'));
    return () => { live = false; };
  }, [open, isPc, facility.institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    setOpening(false);
    setExportLcId('');
    setF({
      drawdownNo: '', drawdownDate: toLocalIsoDate(), amount: '', foreignAmount: '', exchangeRate: '', maturityDate: '',
      repaymentType: 'BULLET', frequency: 'MONTHLY', graceMonths: '0', installments: '', bankAccountId: facility.bankAccountId ?? '', reference: '', remarks: '',
    });
  }, [open, facility]);

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) => setF(s => ({ ...s, [k]: e.target.value }));
  const num = (v: string) => (v === '' ? null : Number(v));
  const needsInstalments = f.repaymentType !== 'BULLET';
  const valid = f.drawdownNo?.trim() && f.drawdownDate && (foreign ? f.foreignAmount && f.exchangeRate : f.amount) &&
    (opening || f.bankAccountId) && (!isPc || opening || exportLcId) && (!needsInstalments || f.installments) && (needsInstalments || f.maturityDate);

  const save = async () => {
    setSaving(true);
    try {
      await BorrowingService.createDrawdown({
        facilityId: facility.id, drawdownNo: f.drawdownNo.trim(), drawdownDate: f.drawdownDate,
        amount: foreign ? null : num(f.amount), foreignAmount: foreign ? num(f.foreignAmount) : null,
        exchangeRate: foreign ? num(f.exchangeRate) : null, maturityDate: f.maturityDate || null,
        repaymentType: f.repaymentType as RepaymentType, frequency: f.frequency as ScheduleFrequency,
        graceMonths: num(f.graceMonths), installments: num(f.installments), bankAccountId: f.bankAccountId || null,
        opening, reference: f.reference || null, remarks: f.remarks || null,
        exportLcId: isPc && !opening ? exportLcId : null,
      });
      toast.showSuccess(opening ? 'Opening drawdown recorded (no journal posted)' : 'Drawdown posted');
      onSaved();
    } catch (err) {
      toast.showApiError(err, 'Could not record the drawdown');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ 'data-help-page': 'fin.drawdown.page' } as any}>
      <DialogTitle>New drawdown on {facility.facilityNo}</DialogTitle>
      <DialogContent>
        <Box data-help-section="fin.drawdown.section.body" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
          <TextField data-help="fin.drawdown.field.number" sx={cell} size="small" label="Drawdown no" required value={f.drawdownNo ?? ''} onChange={set('drawdownNo')} />
          <TextField data-help="fin.drawdown.field.date" sx={cell} size="small" type="date" label="Date" required InputLabelProps={{ shrink: true }} value={f.drawdownDate ?? ''} onChange={set('drawdownDate')} />
          {foreign ? (
            <>
              <TextField data-help="fin.drawdown.field.foreignAmount" sx={cell} size="small" type="number" label={`Amount (${facility.currencyCode})`} required value={f.foreignAmount ?? ''} onChange={set('foreignAmount')} />
              <TextField data-help="fin.drawdown.field.rate" sx={cell} size="small" type="number" label="Exchange rate" required value={f.exchangeRate ?? ''} onChange={set('exchangeRate')} />
            </>
          ) : (
            <TextField data-help="fin.drawdown.field.amount" sx={cell} size="small" type="number" label="Amount" required value={f.amount ?? ''} onChange={set('amount')} />
          )}
          <TextField data-help="fin.drawdown.field.repayment" sx={cell} size="small" select label="Repayment" value={f.repaymentType ?? 'BULLET'} onChange={set('repaymentType')}>
            {REPAYMENT_TYPES.map(t => <MenuItem key={t} value={t}>{humanizeEnum(t)}</MenuItem>)}
          </TextField>
          {needsInstalments ? (
            <>
              <TextField data-help="fin.drawdown.field.frequency" sx={cell} size="small" select label="Frequency" value={f.frequency ?? 'MONTHLY'} onChange={set('frequency')}>
                {FREQUENCIES.map(t => <MenuItem key={t} value={t}>{humanizeEnum(t)}</MenuItem>)}
              </TextField>
              <TextField data-help="fin.drawdown.field.instalments" sx={cell} size="small" type="number" label="Instalments" required value={f.installments ?? ''} onChange={set('installments')} />
              <TextField data-help="fin.drawdown.field.grace" sx={cell} size="small" type="number" label="Grace (months)" value={f.graceMonths ?? ''} onChange={set('graceMonths')} />
            </>
          ) : (
            <TextField data-help="fin.drawdown.field.maturity" sx={cell} size="small" type="date" label="Maturity date" required InputLabelProps={{ shrink: true }} value={f.maturityDate ?? ''} onChange={set('maturityDate')} />
          )}
          <Box sx={cell}>
            <SearchableSelect data-help="fin.drawdown.field.bank" options={bankAccounts} value={f.bankAccountId ?? ''} onChange={id => setF(s => ({ ...s, bankAccountId: id }))}
              getOptionLabel={b => `${b.bankName} ${b.accountNumber}`} label="Credited to bank account" size="small" required={!opening} />
          </Box>
          {isPc && !opening && (
            <Box sx={cell} data-testid="drawdown-export-lc">
              <SearchableSelect data-help="fin.drawdown.field.exportLc" options={lcs} value={exportLcId} onChange={setExportLcId}
                getOptionLabel={l => l.label} label="Export LC (packing credit base)" size="small" required />
            </Box>
          )}
          <TextField data-help="fin.drawdown.field.reference" sx={cell} size="small" label="Reference" value={f.reference ?? ''} onChange={set('reference')} />
          <TextField data-help="fin.drawdown.field.remarks" size="small" label="Remarks" sx={{ flex: '1 1 100%' }} value={f.remarks ?? ''} onChange={set('remarks')} />
        </Box>
        <FormControlLabel data-help="fin.drawdown.field.opening" sx={{ mt: 1 }} control={<Checkbox checked={opening} onChange={e => setOpening(e.target.checked)} />}
          label="Opening balance (already in the GL: records the loan without posting a journal)" />
      </DialogContent>
      <DialogActions>
        <Button data-help="fin.drawdown.cancel" onClick={onClose}>Cancel</Button>
        <Button data-help="fin.drawdown.save" variant="contained" onClick={save} disabled={!valid || saving}>Record drawdown</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DrawdownDialog;
